import config from '@/common/config';
import api from '../../api/manageRidePrivacy';
import billingApi from '../../api/billing';

const state = {
  rideAccesskeySecretKey: null,
  rideAccesskeyRedirectPage: null,
  rideAccesskeyConsultingMeetingCalendarLink: null,
  rideAccesskeyAuthorizedUsersCount: 4,
  rideAccesskeyAction: config.config.RideAccessActions.VIEW,
  recipientDetailsForOneUserAccess: null,
  oneTimeRideAccesskey: null,
  allowSaleViaLocalShop: false,
  rideAccesskeyIsActive: true,

  billingProfile: null, // { billing_mode, prepaid_balance, business_basic_agreement_accepted, ... }
  /** null until /billing/profile loads; true if any business_basic_agreement row exists (business basic terms). */
  businessBasicAgreementAccepted: null,
  ridePriceBreakdown: null, // { total_amount, charged_days, daily_price, ... }
  approval: {
    approved: false,
    transactionId: null,
    loading: false,
    error: null,
  },
};

const getters = {
  rideAccesskeySecretKey: state => state.rideAccesskeySecretKey,
  rideAccesskeyRedirectPage: state => state.rideAccesskeyRedirectPage,
  rideAccesskeyConsultingMeetingCalendarLink: state => state.rideAccesskeyConsultingMeetingCalendarLink,
  rideAccesskeyAuthorizedUsersCount: state => state.rideAccesskeyAuthorizedUsersCount,
  rideAccesskeyAction: state => state.rideAccesskeyAction,
  rideAccesskeyActionIsEdit: state => state.rideAccesskeyAction === config.config.RideAccessActions.COPY,
  recipientDetailsForOneUserAccess: state => state.recipientDetailsForOneUserAccess,
  oneTimeRideAccesskey : state=> state.oneTimeRideAccesskey,
  allowSaleViaLocalShop: state => state.allowSaleViaLocalShop,
  rideAccesskeyIsActive: state => state.rideAccesskeyIsActive,

  billingProfile: state => state.billingProfile,
  ridePriceBreakdown: state => state.ridePriceBreakdown,
  billingMode: state => state.billingProfile?.billing_mode || null,
  prepaidBalance: state => state.billingProfile?.prepaid_balance ?? null,
  isPrepaid: (state, getters) => getters.billingMode === 'prepaid',
  approval: state => state.approval,
  isApprovedForSharing: state => state.approval?.approved === true,
  businessBasicAgreementAccepted: state => state.businessBasicAgreementAccepted,
};

const actions = {
  rideAccesskeyLoadDefault({ commit }) {
    commit('setRideAccesskeySecretKey', null);
    commit('setRideAccesskeyRedirectPage', null);
    commit('setRideAccesskeyConsultingMeetingCalendarLink', null);
    commit('setRideAccesskeyAuthorizedUsersCount', 4);
    commit('setRideAccesskeyAction', config.config.RideAccessActions.VIEW);
    commit('setRideAccesskeyIsActive', true);
    commit('setRecipientDetailsForOneUserAccess', null);
    commit('setOneTimeRideAccesskey', null);
    commit('setAllowSaleViaLocalShop', false);
    commit('setBillingProfile', null);
    commit('setBusinessBasicAgreementAccepted', null);
    commit('setRidePriceBreakdown', null);
    commit('setApprovalState', { approved: false, transactionId: null, loading: false, error: null });
    
  },

  async fetchBillingProfile({ rootState, commit, dispatch }, rideId) {
    const { apptoken } = rootState.appauth;
    commit('setBillingProfile', null);
    commit('setBusinessBasicAgreementAccepted', null);
    commit('setRidePriceBreakdown', null);
    commit('setApprovalState', { approved: false, transactionId: null, loading: false, error: null });

    if (!apptoken || !rideId) {
      return null;
    }

    try {
      const resp = await billingApi.getBillingProfile(apptoken);
      const profile = resp?.data || null;
      commit('setBillingProfile', profile);
      // Any version, as before: drives the ORIGINAL first-time signing flow (prepaid
      // "continue to terms"). Old-version signers are stopped earlier by the
      // re-sign gate (mustSignUpdatedTerms), so they never reach that flow.
      commit(
        'setBusinessBasicAgreementAccepted',
        profile && profile.business_basic_agreement_accepted === true,
      );
      commit('setBusinessBasicAgreementAnyAccepted', !!profile && profile.business_basic_agreement_accepted === true);
      commit('setBusinessBasicAgreementLatestAccepted', !!profile && profile.business_basic_agreement_latest_accepted === true);
      commit('setBusinessBasicAgreementHasPhone', !!profile && profile.has_phone === true);

      if (profile?.billing_mode === 'prepaid') {
        const priceResp = await billingApi.getRidePrice(apptoken, rideId);
        const priceData = priceResp?.data || null;
        commit('setRidePriceBreakdown', priceData);

        if (priceData?.is_paid === true) {
          commit('setApprovalState', {
            approved: true,
            transactionId: priceData.billing_transaction_id || null,
            loading: false,
            error: null,
          });
        }
      }
      return profile;
    } catch (error) {
      // Keep existing UX: show a modal error and fallback to current behavior for non-prepaid
      commit('setBillingProfile', null);
      commit('setBusinessBasicAgreementAccepted', null);
      commit('setRidePriceBreakdown', null);
      dispatch('showModalWithParameters', {
        errorNumber: 'billing_profile_failed',
        type: 'textConverter',
      }, { root: true });
      return null;
    }
  },

  async approvePrepaidRideShare({ state, rootState, commit, dispatch }, rideId) {
    const { apptoken } = rootState.appauth;
    if (!apptoken || !rideId) {
      return { approved: false };
    }

    commit('setApprovalState', { loading: true, error: null });
    try {
      const payload = {
        number_of_users_to_share_with: state.rideAccesskeyAuthorizedUsersCount,
      };
      dispatch('showModal', 'loadingIcon', { root: true });
      const resp = await billingApi.payForRide(apptoken, rideId, payload);
      dispatch('showModal', '', { root: true });
      const data = resp?.data || {};

      if (data.approved !== true) {
        commit('setApprovalState', { loading: false, approved: false, error: data.error || 'not_approved' });
        dispatch('showModalWithParameters', {
          errorNumber: data.error || 'billing_not_approved',
          type: 'textConverter',
        }, { root: true });
        return data;
      }

      commit('setApprovalState', {
        loading: false,
        approved: true,
        transactionId: data.billing_transaction_id || null,
        error: null,
      });

      if (typeof data.new_prepaid_balance !== 'undefined') {
        commit('setBillingProfile', {
          ...(state.billingProfile || {}),
          prepaid_balance: data.new_prepaid_balance,
        });
      }

      const shareInfo = data.ride_share_info || null;
      if (shareInfo) {
        await dispatch('applyRideShareInfoToState', shareInfo);
      }

      return data;
    } catch (error) {
      const serverMsg = error?.response?.data?.message || error?.response?.data?.error || 'billing_payment_failed';
      commit('setApprovalState', { loading: false, approved: false, error: serverMsg });
      dispatch('showModalWithParameters', {
        errorNumber: serverMsg,
        type: 'textConverter',
      }, { root: true });
      return { approved: false, error: serverMsg };
    }
  },

  applyRideShareInfoToState({ rootState, commit }, rideShareInfo) {
    // rideShareInfo uses the same structure as get_assigned_ride_key_full_information "rideData"
    const data = rideShareInfo || {};

    // Get ride language from ride store module
    const rideLanguage = rootState.ride?.rideHeader?.ride?.language ||
                        rootState.ride?.ride?.ride?.language ||
                        null;

    // Normalize language and determine if it's Hebrew
    let isHebrew = false;
    if (rideLanguage) {
      const normalizedLang = rideLanguage.toLowerCase();
      isHebrew = normalizedLang === 'hebrew' || normalizedLang === 'he';
    }

    // Build URL with secret key and add language query parameter if ride language is NOT Hebrew (default to English)
    const regionBase = rootState.regionConfig?.baseUrl || 'https://tripplanner.co.il';
    const baseUrl = 'https://' + (data.subdomain ? data.subdomain + '.' : '') + regionBase.replace('https://', '') + '/assign_to_ride/';
    const languageParam = !isHebrew ? '?lang=en' : '?lang=he';
    const rideUrl = baseUrl + data.secret_key + languageParam;

    commit('setRideAccesskeySecretKey', rideUrl);
    commit('setRideAccesskeyRedirectPage', data.redirect_page);
    commit('setRideAccesskeyConsultingMeetingCalendarLink', data.consulting_meeting_calendar_link);
    if (typeof data.authorized_users_count !== 'undefined') {
      commit('setRideAccesskeyAuthorizedUsersCount', data.authorized_users_count);
    }
    commit('setRideAccesskeyAction', data.action);
    commit('setAllowSaleViaLocalShop', data.allow_sale_via_local_shop || false);
    commit('setRideAccesskeyIsActive', data.is_active !== false);
  },

  async getRideAccessKey({ dispatch, rootState }, rideId ) {
    const { apptoken } = rootState.appauth;
    await dispatch('rideAccesskeyLoadDefault');
    if (apptoken) {
      if ( rideId ){
        const response = await api.getOrCreateFullRideAccesskeyInformation(apptoken, rideId)
        const data = response.data.rideData;
        
        dispatch('applyRideShareInfoToState', data);
      }
    }
  },

  async updateRideAccessKey({ state, rootState }, rideId ) {
    const { apptoken } = rootState.appauth;
    const rideAccessKeyInformation = {
      ride_id: rideId,
      redirectPage: state.rideAccesskeyRedirectPage,
      authorizedUsersCount: state.rideAccesskeyAuthorizedUsersCount,
      action: state.rideAccesskeyAction,
      allowSaleViaLocalShop: state.allowSaleViaLocalShop,
    };
    await api.updateRideAccessKeyInformation(apptoken, rideAccessKeyInformation);    
  },
  async restoreRideAccessLink({ rootState, commit, dispatch }, rideId) {
    const { apptoken } = rootState.appauth;
    await api.restoreRideAccessLink(apptoken, rideId);
    commit('setRideAccesskeyIsActive', true);
    dispatch('showModalWithParameters', { errorNumber: 'ride_link_activated', type: 'textConverter' });
  },

  async getOneUserAccessKey({ state, rootState, commit }, rideId ) {
    const { apptoken } = rootState.appauth;
    const rideOneTimeAccessKeyInformation = {
      ride_id: rideId,
      recipientDetails: state.recipientDetailsForOneUserAccess,
    };
    const response = await api.getOneUserAccessKey(apptoken, rideOneTimeAccessKeyInformation);
    const data = response.data.accessData;
    if( data.status == "success" ){
      commit('setOneTimeRideAccesskey', data.link);
    }
  },
};

const mutations = {
  setRideAccesskeySecretKey: (state, rideAccesskeySecretKey) => {
    state.rideAccesskeySecretKey = rideAccesskeySecretKey;
  },
  setRideAccesskeyRedirectPage: (state, rideAccesskeyRedirectPage) => {
    state.rideAccesskeyRedirectPage = rideAccesskeyRedirectPage;
  },
  setRideAccesskeyConsultingMeetingCalendarLink: (state, rideAccesskeyConsultingMeetingCalendarLink) => {
    state.rideAccesskeyConsultingMeetingCalendarLink = rideAccesskeyConsultingMeetingCalendarLink;
  },
  setRideAccesskeyAuthorizedUsersCount: (state, rideAccesskeyAuthorizedUsersCount) => {
    state.rideAccesskeyAuthorizedUsersCount = rideAccesskeyAuthorizedUsersCount;
  },
  setRideAccesskeyAction: (state, rideAccesskeyAction) => {
    const actionMapping = {
      "true": config.config.RideAccessActions.COPY,
      "false": config.config.RideAccessActions.VIEW,
    };
    const key = String(rideAccesskeyAction);
    state.rideAccesskeyAction = actionMapping[key] !== undefined ? actionMapping[key] : key;
  },
  setRecipientDetailsForOneUserAccess: (state, recipientDetailsForOneUserAccess) => {
    state.recipientDetailsForOneUserAccess = recipientDetailsForOneUserAccess;
  },
  setOneTimeRideAccesskey: (state, oneTimeRideAccesskey) => {
    state.oneTimeRideAccesskey = oneTimeRideAccesskey;
  },
  setAllowSaleViaLocalShop: (state, allowSaleViaLocalShop) => {
    state.allowSaleViaLocalShop = allowSaleViaLocalShop;
  },
  setRideAccesskeyIsActive: (state, value) => {
    state.rideAccesskeyIsActive = value;
  },

  setBillingProfile: (state, billingProfile) => {
    state.billingProfile = billingProfile;
  },
  setRidePriceBreakdown: (state, ridePriceBreakdown) => {
    state.ridePriceBreakdown = ridePriceBreakdown;
  },
  setApprovalState: (state, patch) => {
    state.approval = {
      ...(state.approval || { approved: false, transactionId: null, loading: false, error: null }),
      ...(patch || {}),
    };
  },
  setBusinessBasicAgreementAccepted: (state, value) => {
    state.businessBasicAgreementAccepted = value;
  },

};

export default {
  state,
  getters,
  actions,
  mutations
};
